import { Button, Col, Form, Input, Row, Select, Typography } from "antd";
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import {
  createCoupon,
  editCoupon,
  getAllVariants,
} from "../../../api/AdminRequest";
import { useLocation, useNavigate } from "react-router-dom";

const { Title } = Typography;
const { Option } = Select;

const formItemLayout = {
  labelCol: { xs: { span: 24 }, sm: { span: 8 } },
};
const tailFormItemLayout = {
  wrapperCol: { xs: { span: 24, offset: 0 }, sm: { span: 16, offset: 8 } },
};

const QR_KEYS = ["Luxury", "Metal", "PVC", "PVC_NC", "Wooden"];

// ── Reusable percent input group ──────────────────────────────────────────────
function PercentFields({ form, fieldPrefix }) {
  return QR_KEYS.map((key) => (
    <div key={key} className="grid grid-cols-2 gap-2">
      <label className="text-xl font-semibold">{key} (in %)</label>
      <Form.Item
        name={[fieldPrefix, key]}
        className="mb-2"
        rules={[
          { required: true, message: `Please input ${key}` },
          {
            type: "number",
            min: 0,
            max: 100,
            message: `${key} must be between 0 and 100`,
            transform: (value) => Number(value),
          },
        ]}
      >
        <Input
          type="number"
          className="py-2"
          min={0}
          max={100}
          onKeyDown={(e) => {
            if (["-", "+", "e"].includes(e.key)) e.preventDefault();
          }}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (val > 100) form.setFieldValue([fieldPrefix, key], 100);
            if (val < 0) form.setFieldValue([fieldPrefix, key], 0);
          }}
        />
      </Form.Item>
    </div>
  ));
}

function CreateCoupon({ edit }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [phone, setPhone] = useState(
    edit ? "" + state?.salesPerson?.phone : "971",
  );
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  useEffect(() => {
    const fetchVariants = async () => {
      setVariantsLoading(true);
      try {
        const { data } = await getAllVariants();
        if (data?.success) setVariants(data.variants);
      } catch {
        toast.error("Failed to load variants");
      } finally {
        setVariantsLoading(false);
      }
    };
    fetchVariants();
  }, []);

  const onFinish = async (values) => {
    values.salesPerson.phone = phone;
    const id = state?._id;
    try {
      const { data } = edit
        ? await editCoupon(id, values)
        : await createCoupon(values);

      if (data.success) {
        toast.success("New Coupon Created", {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });
      } else if (data.update) {
        toast.success("Updated Successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });
      }
      setTimeout(() => navigate("/coupons"), 3000);
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div>
      <Row gutter={16}>
        <Col span={12}>
          <Title level={2}>{edit ? "Edit Coupon" : "Create Coupon"}</Title>
        </Col>
      </Row>

      <div className="flex justify-center mt-14 mb-10">
        <Form
          {...formItemLayout}
          form={form}
          name="coupon"
          onFinish={onFinish}
          initialValues={{
            name: state?.name,
            code: state?.code,
            expiryDate: state?.expiryDate,
            BusinessQR: state?.value?.BusinessQR,
            reviewQR: state?.value?.reviewQR,
            digitalInterface: state?.value?.digitalInterface,
            salesPerson: state?.salesPerson,
            applyFor: state?.applyFor,
            applyTo: state?.applyTo?.map((v) => v?._id || v),
          }}
          style={{ width: 600 }}
          scrollToFirstError
        >
          <label className="text-xl font-semibold">Coupon Name</label>
          <Form.Item
            name="name"
            className="mb-2"
            rules={[
              {
                required: true,
                message: "Please input coupon name!",
                whitespace: true,
              },
            ]}
          >
            <Input className="py-2" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xl font-semibold">Coupon Code</label>
              <Form.Item
                name="code"
                className="mb-2"
                rules={[
                  { required: true, message: "Please input coupon code!" },
                ]}
              >
                <Input className="py-2" />
              </Form.Item>
            </div>
            <div>
              <label className="text-xl font-semibold">Expiry Days</label>
              <Form.Item
                name="expiryDate"
                className="mb-2"
                rules={[
                  { required: true, message: "Please input expiry days!" },
                ]}
              >
                <Input type="number" className="py-2" />
              </Form.Item>
            </div>
          </div>

          <label className="text-xl font-semibold">Apply For</label>
          <Form.Item
            name="applyFor"
            className="mb-2"
            rules={[{ required: true, message: "Please select apply for!" }]}
          >
            <Select placeholder="Select region">
              <Option value="global">Global</Option>
              <Option value="IN">India (IN)</Option>
              <Option value="AE">UAE (AE)</Option>
            </Select>
          </Form.Item>

          <label className="text-xl font-semibold">Apply To (Variants)</label>
          <Form.Item name="applyTo" className="mb-4">
            <Select
              mode="multiple"
              allowClear
              placeholder="Select variants (optional)"
              loading={variantsLoading}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  <div
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b font-medium text-blue-700"
                    onMouseDown={(e) => e.preventDefault()} // prevent dropdown from closing
                    onClick={() => {
                      const currentValues = form.getFieldValue("applyTo") || [];
                      const allIds = variants.map((v) => v._id);
                      const isAllSelected = allIds.every((id) =>
                        currentValues.includes(id),
                      );

                      form.setFieldValue(
                        "applyTo",
                        isAllSelected ? [] : allIds,
                      );
                    }}
                  >
                    {(() => {
                      const currentValues = form.getFieldValue("applyTo") || [];
                      const allIds = variants.map((v) => v._id);
                      const isAllSelected =
                        allIds.length > 0 &&
                        allIds.every((id) => currentValues.includes(id));
                      return isAllSelected ? "✕ Deselect All" : "✓ Select All";
                    })()}
                  </div>
                  {menu}
                </>
              )}
            >
              {variants.map((variant) => (
                <Option
                  key={variant._id}
                  value={variant._id}
                  label={`${variant.name} — ${variant.subtittle || ""}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{variant.name}</span>
                    {variant.subtittle && (
                      <span className="text-xs text-gray-400">
                        {variant.subtittle}
                      </span>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <h2 className="font-bold text-xl my-2">Business QR Values:</h2>
          <PercentFields form={form} fieldPrefix="BusinessQR" />

          <h2 className="font-bold text-xl my-2">Review QR Values:</h2>
          <PercentFields form={form} fieldPrefix="reviewQR" />

          <h2 className="font-bold text-xl my-2">Digital Interface Value:</h2>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xl font-semibold">
              Digital Interface (in %)
            </label>
            <Form.Item
              name="digitalInterface"
              className="mb-2"
              rules={[
                {
                  required: true,
                  message: "Please input digital interface value!",
                },
                {
                  type: "number",
                  min: 0,
                  max: 100,
                  message: "Digital Interface must be between 0 and 100",
                  transform: (value) => Number(value),
                },
              ]}
            >
              <Input
                type="number"
                className="py-2"
                min={0}
                max={100}
                onKeyDown={(e) => {
                  if (["-", "+", "e"].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val > 100) form.setFieldValue("digitalInterface", 100);
                  if (val < 0) form.setFieldValue("digitalInterface", 0);
                }}
              />
            </Form.Item>
          </div>

          <h2 className="font-bold text-xl my-2">Sales Person Details</h2>
          <label className="text-xl font-semibold">Name</label>
          <Form.Item
            name={["salesPerson", "name"]}
            className="mb-2"
            rules={[
              {
                required: true,
                message: "Please input sales person name!",
                whitespace: true,
              },
            ]}
          >
            <Input className="py-2" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xl font-semibold">E-mail</label>
              <Form.Item
                name={["salesPerson", "email"]}
                className="mb-2"
                rules={[
                  { type: "email", message: "Invalid E-mail!" },
                  { required: true, message: "Please input E-mail!" },
                ]}
              >
                <Input className="py-2" />
              </Form.Item>
            </div>
            <div>
              <label className="text-xl font-semibold">Phone Number</label>
              <PhoneInput
                country="ae"
                enableSearch
                value={phone}
                onChange={(val) => setPhone(val)}
                inputStyle={{ width: "100%", height: "34px" }}
              />
            </div>
          </div>

          <Form.Item
            {...tailFormItemLayout}
            className="flex justify-center mr-14"
          >
            <Button
              className="bg-blue-800 w-32 my-5"
              type="primary"
              htmlType="submit"
            >
              {edit ? "Save" : "Register"}
            </Button>
          </Form.Item>
        </Form>

        <ToastContainer />
      </div>
    </div>
  );
}

export default CreateCoupon;
